import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import { Timeline, TimelineEvent } from 'react-event-timeline';
import { getMessages, sendMessage, updateViewed } from '../../services/followup';
import Button from '../Button';
import { get } from '../../services/storage';

import { MessageContainer, MessageBlockContainer, MessageBlockTitle } from './styles';
import ClaimFollowUpPendencyFields, {
  ClaimPendencyValues
} from '../ClaimFollowUpPendencyFields';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../services/notification';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import styled from 'styled-components';
import { hasPermission } from '../AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { set } from 'lodash';
import { FaFire } from 'react-icons/fa';
import mailReadIcon from '../../resources/imgs/mail-read-filled.svg';
import mailUnreadIcon from '../../resources/imgs/mail-unread-filled.svg';

export type MessageType = {
  id?: string;
  text: string;
  viewed?: boolean;
  pendingResponsible?: string | null;
  pendingType?: string | null;
  pending_responsible?: string | null;
  pending_type?: string | null;
  senderId: {
    name:
    | boolean
    | React.ReactChild
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
    role: string;
  };
  createdAt: moment.MomentInput;
};

const Div = styled.div`
  strong {
    font-weight: bold;
  }
`;

const MessageTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const PendencyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #707682;
  font-size: 12px;
  font-weight: 400;
`;

const PendencyBadge = styled.span<{ $filled?: boolean; $accentColor: string }>`
  border: 1px solid ${props => props.$accentColor};
  border-radius: 999px;
  background: ${props => (props.$filled ? props.$accentColor : '#ffffff')};
  color: ${props => (props.$filled ? '#ffffff' : props.$accentColor)};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 12px;
`;

const MessageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const ViewedToggleButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c9cdd3;
    box-shadow: 0 1px 4px rgba(47, 52, 65, 0.14);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  img {
    width: 18px;
    height: 18px;
  }
`;

const FollowUp = (props: {
  messages: MessageType[];
  id: string;
  page: number;
  totalMessages: number;
  isBlocked?: boolean;
  accentColor?: string;
  hideSendIcon?: boolean;
  showComposerDivider?: boolean;
  showPendingFields?: boolean;
  setPage: (page: number) => void;
  onClaimFinalized?: () => void;
}) => {
  const [isContentLoading, setIsContentLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(props.messages);
  const [message, setMessage] = useState('');
  const id = props.id;
  const claim_id = id;
  const user_id = get('uid');
  const userName = get('name');
  const userEmail = get('email');
  const userRole = get('role');
  const canManagePendencyFields =
    userRole === UserRolesEnum.brokerAdmin || userRole === UserRolesEnum.brokerAnalyst;
  const showPendencyFields = Boolean(props.showPendingFields && canManagePendencyFields);

  const [userCanSendMessage, setUserCanSendMessage] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [pendency, setPendency] = useState<ClaimPendencyValues>({});
  const [viewedMessageLoadingId, setViewedMessageLoadingId] = useState<string | null>(
    null
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const manualUnreadMessageIdsRef = useRef<Set<string>>(new Set());

  const { page, setPage } = props;
  const accentColor = props.accentColor || '#5C6BC0';

  const perPage = 50;
  const totalPages = Math.ceil(props.totalMessages! / perPage);

  const handleScroll = async () => {
    const el = scrollRef.current;
    if (!el || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore && !isContentLoading) {
      try {
        setIsContentLoading(true);
        const currentPage = page || 1;
        const nextPage = currentPage + 1;
        setPage(nextPage);
        const response = await getMessages(id, nextPage);
        const { data } = response;
        setMessages(prevMessages => [...prevMessages, ...data]);
      } catch (error) {
        console.error('Erro ao carregar mais mensagens:', error);
        dangerNotification(
          'Oops...',
          'Não foi possível carregar mais mensagens, tente novamente mais tarde.'
        );
      } finally {
        setIsContentLoading(false);
      }
    }
  };

  const MessageBlocked = () => {
    return (
      <MessageBlockContainer>
        <MessageBlockTitle>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '3px'
            }}
          >
            <FaFire />
          </div>
          Sinistro finalizado
        </MessageBlockTitle>
        <div>
          O sinistro já está finalizado e não é possível enviar novas mensagens, mas você
          ainda pode consultar os registros anteriores.
        </div>
        <div>
          Se precisar de ajuda, entre em contato com LadoBom em
          sinistro.fianca@ladobom.com
        </div>
      </MessageBlockContainer>
    );
  };

  useEffect(() => {
    const has = hasPermission(ActionEnum.create, FeatureEnum.followups);
    setUserCanSendMessage(has);
    if (messages && messages.length > 0) {
      setHasMore(totalPages > 1);
    }
  }, []);

  useEffect(() => {
    setHasMore(totalPages > page);
  }, [page]);

  useEffect(() => {
    const notSeen = messages.filter((message: any) => !message.viewed);
    let willUpdate = [];
    if (userRole?.includes('ESTATE')) {
      willUpdate = notSeen.filter(
        (message: any) =>
          ![UserRolesEnum.estateAdmin, UserRolesEnum.estateUser].includes(
            message.senderId.role
          ) && !manualUnreadMessageIdsRef.current.has(message.id)
      );
    } else {
      // const claimUsersIds = [
      //   'a4c0ba47-fa8d-47a8-a2f5-f4c1844a9b2e', // Fernanda Medeiros
      //   '3afa044e-cf3b-4ed2-b3f6-435200de8313', // Juliana Loch
      //   '5e697f16-983d-4259-81fe-89195c97ee32', // Julia Maria Rodrigues
      //   'be905980-c1d2-4497-8ac9-178e673cef96', // Bruna Araujo
      //   '6a83e5de-cde2-42fd-968b-dc3d66260a64', // Roque Junior
      //   '5ee05efc-54f7-4349-9ed5-916d6581b5cd', // Fernanda Roling
      //   '2959602b-ca18-47ea-b2cd-f01b54c6856d', // Roque
      //   'e1ab81be-e2fa-449a-9418-179956677e0e' // Renan Vidal
      // ];

      const estateMessages = notSeen.filter(
        (message: any) =>
          message.senderId?.role === UserRolesEnum.estateAdmin ||
          message.senderId?.role === UserRolesEnum.estateUser
      );

      /// apenas o resposnsável, ou, os usuários que estão na lista de usuários da reivindicação
      willUpdate = estateMessages.filter((message: any) => {
        if (manualUnreadMessageIdsRef.current.has(message.id)) {
          return false;
        }

        if (user_id === message.claimEstateId.responsibleId) {
          return true;
        } else {
          ///return claimUsersIds.includes(user_id!);
          return userEmail?.includes('@imobseguros.com.br');
        }
      });
    }

    willUpdate.length > 0 &&
      successNotification('Lido', `${willUpdate.length} mensagens marcadas como lidas!`);
    for (const message of willUpdate) {
      if (message.id && user_id) {
        updateViewed(message.id, user_id);
      }
    }
    if (willUpdate.length > 0) {
      const viewedIds = new Set(willUpdate.map((message: MessageType) => message.id));
      setMessages(prevMessages =>
        prevMessages.map(message =>
          message.id && viewedIds.has(message.id)
            ? {
              ...message,
              viewed: true
            }
            : message
        )
      );
    }
  }, [messages]);

  const handleSubmit = async () => {
    if (!message.trim() || !message || message.length < 1) {
      warningNotification('Oops...', 'Por favor, digite uma mensagem antes de enviar.');
    } else {
      try {
        setLoading(true);
        await sendMessage(claim_id, user_id!, message, userName!, pendency);
        setPendency({});
        const response = await getMessages(id, 1);
        const { data, count } = response;
        setMessages(data || []);
        setHasMore(count > perPage);
        setPage(1);
        successNotification('Sucesso!', 'Mensagem enviada!');
      } catch (error) {
        console.log('ERRO AO ENVIAR MENSAGEM:', error);

        const err = error as { statusCode?: number; message?: string };
        if (
          err.statusCode === 403 &&
          err.message ===
          'Esse sinistro foi finalizado, Se precisar de ajuda, entre em contato com LadoBom em sinistro.fianca@ladobom.com'
        ) {
          warningNotification('Mensagem não salva', err.message);
          if (props.onClaimFinalized) {
            props.onClaimFinalized();
          }
          return;
        }

        dangerNotification(
          'Oops...',
          'Não foi possível enviar a mensagem, tente novamente mais tarde.'
        );
      } finally {
        setMessage('');
        setLoading(false);
      }
    }
  };

  const modules = {
    toolbar: [
      [{ align: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['normal', 'large', 'huge'] }],
      [{ color: [] }, { background: [] }]
    ]
  };

  const getPendencyResponsible = (message: MessageType) =>
    message.pendingResponsible || message.pending_responsible;

  const getPendencyType = (message: MessageType) =>
    message.pendingType || message.pending_type;

  const handleToggleViewed = async (messageToUpdate: MessageType) => {
    if (!messageToUpdate.id || !user_id || viewedMessageLoadingId) return;

    const nextViewed = !messageToUpdate.viewed;

    try {
      setViewedMessageLoadingId(messageToUpdate.id);
      await updateViewed(messageToUpdate.id, user_id, nextViewed);

      if (nextViewed) {
        manualUnreadMessageIdsRef.current.delete(messageToUpdate.id);
      } else {
        manualUnreadMessageIdsRef.current.add(messageToUpdate.id);
      }

      setMessages(prevMessages =>
        prevMessages.map(message =>
          message.id === messageToUpdate.id
            ? {
              ...message,
              viewed: nextViewed
            }
            : message
        )
      );
    } catch (error) {
      console.log('ERRO AO ATUALIZAR VISUALIZAÇÃO DA MENSAGEM:', error);
      dangerNotification(
        'Oops...',
        'Não foi possível atualizar a visualização da mensagem, tente novamente mais tarde.'
      );
    } finally {
      setViewedMessageLoadingId(null);
    }
  };

  return (
    <Div>
      {props.isBlocked ? (
        <MessageBlocked />
      ) : (
        <MessageContainer
          $withBottomDivider={props.showComposerDivider}
          $compactEditor={showPendencyFields}
        >
          <div className="quilljs">
            <ReactQuill
              theme="snow"
              modules={modules}
              value={message}
              onChange={value => setMessage(value)}
              readOnly={loading || !userCanSendMessage || props.isBlocked}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: showPendencyFields ? 'space-between' : 'flex-end',
              gap: 20,
              width: '100%'
            }}
          >
            {showPendencyFields && (
              <ClaimFollowUpPendencyFields
                values={pendency}
                disabled={loading || !userCanSendMessage || props.isBlocked}
                onChange={setPendency}
              />
            )}
            <Button
              primary
              content="Enviar"
              labelPosition={props.hideSendIcon ? undefined : 'right'}
              icon={props.hideSendIcon ? undefined : 'send'}
              style={props.hideSendIcon ? { minWidth: 136, textAlign: 'center' } : undefined}
              onClick={handleSubmit}
              loading={loading}
              disabled={loading || !userCanSendMessage || props.isBlocked}
            />
          </div>
        </MessageContainer>
      )}

      <div
        style={{
          overflowY: 'scroll',
          maxHeight: showPendencyFields ? 'max(160px, calc(100vh - 520px))' : '50vh'
        }}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {isContentLoading && (
          <Dimmer active={isContentLoading} inverted>
            <Loader content="Carregando mensagens..." />
          </Dimmer>
        )}

        <Timeline>
          {messages.length &&
            messages.map((message: MessageType) => {
              return (
                <TimelineEvent
                  key={message.id}
                  title={
                    <MessageTitle>
                      <span>{message.senderId.name ? message.senderId.name : userName}</span>
                      <MessageHeaderActions>
                        {(getPendencyResponsible(message) || getPendencyType(message)) && (
                          <PendencyInfo>
                            <span>Pendência:</span>
                            {getPendencyResponsible(message) && (
                              <PendencyBadge $filled $accentColor={accentColor}>
                                {getPendencyResponsible(message)}
                              </PendencyBadge>
                            )}
                            {getPendencyType(message) && (
                              <PendencyBadge $accentColor={accentColor}>
                                {getPendencyType(message)}
                              </PendencyBadge>
                            )}
                          </PendencyInfo>
                        )}
                        <ViewedToggleButton
                          type="button"
                          title={message.viewed ? 'Marcar como não lido' : 'Marcar como lido'}
                          aria-label={
                            message.viewed ? 'Marcar como não lido' : 'Marcar como lido'
                          }
                          disabled={viewedMessageLoadingId === message.id}
                          onClick={event => {
                            event.stopPropagation();
                            handleToggleViewed(message);
                          }}
                        >
                          <img
                            src={message.viewed ? mailReadIcon : mailUnreadIcon}
                            alt=""
                            aria-hidden="true"
                          />
                        </ViewedToggleButton>
                      </MessageHeaderActions>
                    </MessageTitle>
                  }
                  createdAt={moment(message.createdAt).format('DD/MM/YY - HH:mm')}
                  icon={<Icon name="envelope open outline" size="large" />}
                  iconColor={accentColor}
                  style={{
                    backgroundColor: '#fff',
                    padding: 10,
                    boxShadow: `0 0 3px 1px ${accentColor}`
                  }}
                  iconStyle={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 2
                  }}
                  contentStyle={{
                    backgroundColor: '#FEFEFE',
                    color: accentColor
                  }}
                >
                  <ReactQuill theme="bubble" value={message.text} readOnly={true} />
                </TimelineEvent>
              );
            })}
        </Timeline>
      </div>
    </Div>
  );
};

export default FollowUp;

{
  /* <Item.Group>
      {messages.map((message: MessageType) => {
        return (
          <Item>
            <Item.Content>
              <Item.Header>{message.text}</Item.Header>
              <ContainerInfo>
                <Item.Meta><span>{moment(message.createdAt).format('DD/MM/YY - HH:mm:ss')}</span></Item.Meta>
                <Item.Description><p>{message.senderId.name}</p></Item.Description>
              </ContainerInfo>
            </Item.Content>
          </Item>
        )
      })}
    </Item.Group> */
}
